import { useEffect, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  noop
} from '@tanstack/react-query';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthnAutofill,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON
} from '@simplewebauthn/browser';
import * as networking from '../lib/networking';
import { useAuth } from '../hooks/auth';
import { RhinoContextType, RhinoRecordIdentifier } from '../index';

export const PASSKEYS_PATH = '/api/auth/passkeys';
export const passkeysQueryKey = ['authPasskeys'];
export const passkeysSignalQueryKey = ['authPasskeysSignal'];

interface PasskeySignalData {
  userId?: string;
  rpId?: string;
  allAcceptedCredentialIds?: string[];
}

// Best-effort: tell the passkey provider to drop a credential the server does not
// recognize (the 404 branch of sign_in). Feature-detected and fire-and-forget; never
// blocks or surfaces an error. No-op on browsers without the Signal API.
const signalUnknownCredential = ({
  rpId,
  credentialId
}: {
  rpId: string;
  credentialId: string;
}) => {
  if (typeof window === 'undefined') return;
  const signal = window.PublicKeyCredential?.signalUnknownCredential;
  if (typeof signal !== 'function') return;
  Promise.resolve(
    signal.call(window.PublicKeyCredential, { rpId, credentialId })
  ).catch(() => {});
};

export const usePasskeys = () =>
  useQuery({
    queryKey: passkeysQueryKey,
    queryFn: () =>
      networking.networkApiCallOnlyData(PASSKEYS_PATH, { method: 'get' })
  });

// Prefetch from a TanStack Router loader so passkeys are in the React Query
// cache before <PasskeysSettings> mounts (nested under <SignInSettings>). The
// key matches usePasskeys, so the component's useQuery deduplicates and renders
// without a loading spinner.
export const prefetchPasskeys = (rhino: RhinoContextType) =>
  rhino.queryClient
    .query({
      queryKey: passkeysQueryKey,
      queryFn: () =>
        networking.networkApiCallOnlyData(PASSKEYS_PATH, { method: 'get' })
    })
    .then(noop, noop);

// Keeps the passkey provider in sync with the server's COMPLETE valid set via
// PublicKeyCredential.signalAllAcceptedCredentials. Feature-detected: unsupported
// browsers never fetch. Driven from the passkey settings section (mount + after
// add/remove via invalidation). Best-effort; never blocks.
export const usePasskeySignal = () => {
  const supported =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential?.signalAllAcceptedCredentials ===
      'function';

  const { data } = useQuery({
    queryKey: passkeysSignalQueryKey,
    queryFn: () =>
      networking.networkApiCallOnlyData(`${PASSKEYS_PATH}/signal`, {
        method: 'get'
      }) as Promise<PasskeySignalData>,
    enabled: supported
  });

  useEffect(() => {
    // No webauthn_id => the user never registered a passkey; nothing to prune.
    if (!supported || !data?.userId) return;

    Promise.resolve(
      window.PublicKeyCredential.signalAllAcceptedCredentials({
        rpId: data.rpId!,
        userId: data.userId,
        allAcceptedCredentialIds: data.allAcceptedCredentialIds ?? []
      })
    ).catch(() => {});
  }, [supported, data]);
};

export const useRegisterPasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nickname?: string) => {
      const optionsJSON = (await networking.networkApiCallOnlyData(
        `${PASSKEYS_PATH}/registration_options`,
        { method: 'post' }
      )) as PublicKeyCredentialCreationOptionsJSON;

      const publicKeyCredential = await startRegistration({ optionsJSON });

      return networking.networkApiCallOnlyData(PASSKEYS_PATH, {
        method: 'post',
        data: { publicKeyCredential, nickname }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: passkeysQueryKey });
      queryClient.invalidateQueries({ queryKey: passkeysSignalQueryKey });
    }
  });
};

export const useRemovePasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: RhinoRecordIdentifier) =>
      networking.networkApiCallOnlyData(`${PASSKEYS_PATH}/${id}`, {
        method: 'delete'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: passkeysQueryKey });
      queryClient.invalidateQueries({ queryKey: passkeysSignalQueryKey });
    }
  });
};

export const useSignInWithPasskey = () => {
  const { logIn } = useAuth();

  return useMutation({
    // Pass { autofill: true } to drive the conditional-UI (browser autofill)
    // ceremony instead of the default modal dialog. The mode scopes the server's
    // single-use challenge cookie so this ceremony can't clobber a concurrent one.
    mutationFn: async (variables?: { autofill?: boolean }) => {
      const useBrowserAutofill = variables?.autofill === true;
      const mode = useBrowserAutofill ? 'autofill' : 'interactive';

      const optionsJSON = (await networking.networkApiCallOnlyData(
        `${PASSKEYS_PATH}/sign_in_options`,
        { method: 'post', data: { mode } }
      )) as PublicKeyCredentialRequestOptionsJSON;

      const publicKeyCredential = await startAuthentication({
        optionsJSON,
        useBrowserAutofill
      });

      try {
        return await networking.networkApiCallOnlyData(
          `${PASSKEYS_PATH}/sign_in`,
          {
            method: 'post',
            data: { publicKeyCredential, mode }
          }
        );
      } catch (err) {
        if (err instanceof networking.NetworkParamError && err.status === 404) {
          signalUnknownCredential({
            rpId: optionsJSON.rpId!,
            credentialId: publicKeyCredential.id
          });
        }
        throw err;
      }
    },
    // sign_in renders { data: <user> }; hand the user to the auth context.
    onSuccess: (body) =>
      logIn((body as { data: Parameters<typeof logIn>[0] }).data)
  });
};

// Conditional UI: start a discoverable-credential authentication on mount so the
// browser offers saved passkeys inline in any field marked
// autocomplete="... webauthn". No-op on browsers without autofill support. The
// returned mutation can still be triggered manually for the modal fallback.
export const usePasskeyAutofillSignIn = () => {
  const signIn = useSignInWithPasskey();
  const { mutate } = signIn;

  useEffect(() => {
    let active = true;

    browserSupportsWebAuthnAutofill().then((supported) => {
      if (supported && active) mutate({ autofill: true });
    });

    return () => {
      active = false;
    };
  }, [mutate]);

  return signIn;
};

// True when the browser exposes the native request-options/credential JSON
// helpers that immediate mediation relies on. We bypass @simplewebauthn/browser
// for immediate mode because it only supports mediation: "conditional". Actual
// support for the "immediate" value is gated separately on the immediateGet
// client capability (older builds reject it as an unknown enum value).
const supportsImmediateMediation = () =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  typeof window.PublicKeyCredential.parseRequestOptionsFromJSON ===
    'function' &&
  typeof window.PublicKeyCredential.getClientCapabilities === 'function';

// Immediate mediation: ask the browser to show the passkey picker right away,
// but ONLY if a discoverable credential is actually available. If none is, the
// browser rejects without any UI (no dead-end QR/modal), so the normal form is
// left untouched.
export const useSignInWithPasskeyImmediate = () => {
  const { logIn } = useAuth();

  return useMutation({
    mutationFn: async () => {
      // Passive ceremony: scope the challenge cookie to "autofill" so it never
      // collides with the interactive (modal button) ceremony.
      const optionsJSON = (await networking.networkApiCallOnlyData(
        `${PASSKEYS_PATH}/sign_in_options`,
        { method: 'post', data: { mode: 'autofill' } }
      )) as PublicKeyCredentialRequestOptionsJSON;

      const publicKey =
        window.PublicKeyCredential.parseRequestOptionsFromJSON(optionsJSON);

      // Native get() — @simplewebauthn/browser can't pass the immediate UI mode.
      // Note: the Nov 2025 spec update moved this from mediation: "immediate" to
      // a sibling uiMode: "immediate" field, which is not yet in the TS DOM lib.
      // A NotAllowedError means no locally available passkey (immediate skips
      // hybrid/cross-device); the caller treats that as a silent no-op.
      const credential = await navigator.credentials.get({
        publicKey,
        uiMode: 'immediate'
      } as CredentialRequestOptions);

      const publicKeyCredential = (credential as PublicKeyCredential).toJSON();
      try {
        return await networking.networkApiCallOnlyData(
          `${PASSKEYS_PATH}/sign_in`,
          {
            method: 'post',
            data: { publicKeyCredential, mode: 'autofill' }
          }
        );
      } catch (err) {
        if (err instanceof networking.NetworkParamError && err.status === 404) {
          signalUnknownCredential({
            rpId: optionsJSON.rpId!,
            credentialId: publicKeyCredential.id
          });
        }
        throw err;
      }
    },
    onSuccess: (body) =>
      logIn((body as { data: Parameters<typeof logIn>[0] }).data)
  });
};

// Cross-browser passive sign-in on mount. Prefers Chrome's immediate UI mode
// (instant picker when a local passkey exists, detected via the immediateGet
// client capability) and falls back to conditional UI (autofill) on browsers
// without it, e.g. Safari. Where neither is supported nothing fires and the
// modal "Sign in with a passkey" button remains the only entry point. Only one
// ceremony is ever started (they can't run concurrently).
export const usePasskeyAutoSignIn = () => {
  const immediate = useSignInWithPasskeyImmediate();
  const autofill = useSignInWithPasskey();
  const { mutate: immediateMutate } = immediate;
  const { mutate: autofillMutate } = autofill;
  const startedRef = useRef(false);

  useEffect(() => {
    let active = true;

    const start = async () => {
      if (startedRef.current) return;

      // Chrome: immediate UI mode, gated on the immediateGet capability. A
      // getClientCapabilities() failure must not skip the autofill fallback.
      let immediateSupported = false;
      if (supportsImmediateMediation()) {
        try {
          const caps = await window.PublicKeyCredential.getClientCapabilities();
          immediateSupported = !!caps?.immediateGet;
        } catch {
          immediateSupported = false;
        }
      }
      if (!active || startedRef.current) return;

      if (immediateSupported) {
        startedRef.current = true;
        immediateMutate();
        return;
      }

      // Safari and others: conditional UI (autofill).
      const supportsAutofill = await browserSupportsWebAuthnAutofill();
      if (!active || startedRef.current) return;
      if (supportsAutofill) {
        startedRef.current = true;
        autofillMutate({ autofill: true });
      }
    };

    start();

    return () => {
      active = false;
    };
  }, [immediateMutate, autofillMutate]);

  return { immediate, autofill };
};
